import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  FreelancerBreakpoints,
  FreelancerBreakpointValues,
} from '@freelancer/ui/breakpoints';
import { HeadingType, HeadingWeight } from '@freelancer/ui/heading';
import { TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Sidebar } from './sidebar.service';

@Component({
  selector: 'fl-sidebar',
  template: `
    <div
      class="SidebarOverlay"
      [flHide]="(isSidebarOpen$ | flAsync) === false"
      [flShowTablet]="true"
      (click)="toggleSidebar()"
    ></div>
    <div
      #container
      class="SidebarContainer"
      [attr.data-is-open]="isSidebarOpen$ | flAsync"
    >
      <div
        class="SidebarInner"
        [attr.data-is-open]="isSidebarOpen$ | flAsync"
      >
        <!-- view header for mobile only -->
        <fl-view-header
          class="ViewHeader"
          [showBackButton]="false"
          [flShowMobile]="true"
        >
          <div class="ViewHeader-inner">
            <div
              class="ViewHeader-title"
              [flMarginRight]="'small'"
            >
              <fl-heading
                [headingType]="HeadingType.H3"
                [size]="subheading ? TextSize.SMALL : TextSize.LARGE"
                [truncate]="true"
                [flMarginBottom]="subheading ? 'xxxsmall' : 'none'"
              >
                <ng-container
                  *ngIf="heading; else defaultHeading"
                  [ngTemplateOutlet]="heading"
                ></ng-container>
              </fl-heading>
              <fl-heading
                *ngIf="subheading"
                [headingType]="HeadingType.H4"
                [size]="TextSize.XXSMALL"
                [weight]="HeadingWeight.NORMAL"
                [truncate]="true"
              >
                <ng-container [ngTemplateOutlet]="subheading"></ng-container>
              </fl-heading>
            </div>
            <fl-icon
              class="ViewHeader-closeBtn"
              [flShowMobile]="true"
              [name]="'ui-close'"
              (click)="toggleSidebar()"
            ></fl-icon>
          </div>
        </fl-view-header>

        <div class="SidebarContent">
          <div class="SidebarContentInner">
            <fl-scrollable-content
              class="SidebarScroll"
              [hideHorizontalScroll]="true"
            >
              <!-- heading for tablet and up -->
              <fl-heading
                class="SidebarContentTitle"
                [flHideMobile]="true"
                [headingType]="HeadingType.H3"
                [size]="TextSize.MARKETING_SMALL"
                [weight]="HeadingWeight.BOLD"
                [flMarginBottom]="'xsmall'"
              >
                <ng-container
                  *ngIf="heading; else defaultHeading"
                  [ngTemplateOutlet]="heading"
                ></ng-container>
              </fl-heading>
              <fl-sidebar-item
                class="SidebarTitlePlaceholder"
                [iconName]="'ui-bars-v2'"
              ></fl-sidebar-item>
              <ng-content></ng-content>
            </fl-scrollable-content>
          </div>
          <div class="SidebarFooter">
            <ng-content select="fl-sidebar-footer"></ng-content>
          </div>
        </div>
      </div>
    </div>
    <ng-template #defaultHeading>
      <ng-container i18n="Sidebar default heading">Menu</ng-container>
    </ng-template>
  `,
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnDestroy, OnInit {
  HeadingType = HeadingType;
  HeadingWeight = HeadingWeight;
  TextSize = TextSize;

  @Input() heading?: TemplateRef<any>;
  @Input() subheading?: TemplateRef<any>;

  isSidebarOpen$: Observable<boolean>;
  private subscriptions = new Subscription();

  @ViewChild('container', { read: ElementRef }) container: ElementRef;

  private get isDesktopSmall(): boolean {
    return (
      window.innerWidth >=
        parseInt(FreelancerBreakpointValues.DESKTOP_SMALL, 10) &&
      window.innerWidth <=
        parseInt(FreelancerBreakpointValues.DESKTOP_LARGE, 10)
    );
  }

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private sidebar: Sidebar,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  @HostListener('mouseenter')
  handleMouseEnter(): void {
    if (this.isDesktopSmall) {
      this.sidebar.toggle(true);
    }
  }

  @HostListener('mouseleave')
  handleMouseLeave(): void {
    if (this.isDesktopSmall) {
      this.sidebar.toggle(false);
    }
  }

  ngOnInit(): void {
    this.isSidebarOpen$ = this.sidebar.isOpen();

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe(() => {
          this.sidebar.toggle(false);
        }),
    );

    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        this.breakpointObserver
          .observe(FreelancerBreakpoints.DESKTOP_LARGE)
          .subscribe(isDesktopLarge => {
            this.sidebar.toggle(isDesktopLarge.matches);
          }),
      );
    }
  }

  toggleSidebar(): void {
    this.sidebar.toggle();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
